/**
 * AddEventStaffDialog
 *
 * Modal launched from the EventStaffPanel "Add Staff" button. Lets an
 * authorized operator attach an existing auth user to an active
 * reunification event via `useCreateEventStaff` (POST
 * `/events/{event_public_id}/staff/`).
 *
 * The picker (StaffPicker) excludes users already on the event so the
 * operator can't double-add. Role is required; notes are optional.
 * Station assignment is out of scope here — the picker for stations
 * lives behind a deep nested route (`events/{id}/sites/{site_id}/
 * floorplan-mappings/{mapping_id}/stations/`) the SMS app doesn't
 * surface. Station can still be cleared from StaffDetailDrawer once a
 * record exists.
 *
 * Permission gating happens in the parent panel via FeatureCode.REUNIFICATION
 * — the backend's `CanManageStaffAssignments` is the load-bearing check
 * and will 403 a manual API call even with the feature flag flipped.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Field,
  HStack,
  Portal,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Button, SelectInput } from '@themes/aegix';
import { useCreateEventStaff } from '../../hooks/queries/useReunification';
import { toaster } from '../ui/toaster';
import { STAFF_ROLE_OPTIONS } from '../../types/ReunificationTypes';
import type {
  EventStaffAssignment,
  StaffRole,
} from '../../types/ReunificationTypes';
import { StaffPicker, type StaffPickerSelection } from './StaffPicker';

const DEFAULT_ROLE_PLACEHOLDER = '';

export interface AddEventStaffDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog should close (cancel, escape, backdrop click). */
  onClose: () => void;
  /** Reunification event public_id receiving the new assignment. */
  eventId: string;
  /**
   * Auth-user ids that are already on the event; excluded from the picker
   * so the operator can't create duplicate assignments.
   */
  existingStaffUserIds?: number[];
  /** Called after a successful create with the new assignment row. */
  onCreated?: (created: EventStaffAssignment) => void;
}

export function AddEventStaffDialog({
  isOpen,
  onClose,
  eventId,
  existingStaffUserIds,
  onCreated,
}: AddEventStaffDialogProps) {
  const [selected, setSelected] = useState<StaffPickerSelection | null>(null);
  const [role, setRole] = useState<string>(DEFAULT_ROLE_PLACEHOLDER);
  const [notes, setNotes] = useState('');

  const createMutation = useCreateEventStaff(eventId);
  const submitting = createMutation.isPending;

  // Reset state every time the dialog opens — avoid leaking the prior pick
  // into a subsequent add.
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      setRole(DEFAULT_ROLE_PLACEHOLDER);
      setNotes('');
    }
  }, [isOpen]);

  const canSubmit = useMemo(
    () => !!selected && !!role && !submitting,
    [selected, role, submitting],
  );

  async function handleSubmit() {
    if (!selected || !role) return;
    try {
      const created = await createMutation.mutateAsync({
        staff_member: selected.userId,
        role: role as StaffRole,
        notes: notes.trim() || undefined,
      });
      toaster.create({
        title: 'Staff added',
        description: `${selected.name} assigned as ${
          STAFF_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role
        }.`,
        type: 'success',
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not add staff',
        description: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open && !submitting) onClose();
      }}
      placement="center"
      size="md"
      closeOnInteractOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bgColor="surface.primary"
            borderRadius="lg"
            maxWidth="560px"
            data-e2e="add-event-staff-dialog"
          >
            <Dialog.Header p="20px 20px 8px 20px">
              <Dialog.Title
                fontSize="16px"
                fontWeight={600}
                color="text.primary"
              >
                Add staff to event
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body p="0 20px 16px 20px">
              <VStack align="stretch" gap={4}>
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="text.secondary"
                    mb={2}
                  >
                    Staff Member
                  </Text>
                  <StaffPicker
                    selected={selected}
                    onSelect={setSelected}
                    excludeUserIds={existingStaffUserIds}
                    dataE2ePrefix="add-event-staff-picker"
                  />
                </Box>

                <SelectInput
                  label="Role"
                  name="role"
                  value={role}
                  onChange={(val: string) => setRole(val)}
                  option={STAFF_ROLE_OPTIONS}
                  placeholder="Select a role"
                  e2eTitle="add-event-staff-role"
                />

                <Field.Root>
                  <Field.Label fontSize="sm" color="text.primary">
                    Notes (optional)
                  </Field.Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any context the team should know…"
                    rows={3}
                    disabled={submitting}
                    data-e2e="add-event-staff-notes"
                  />
                </Field.Root>

                <Text fontSize="xs" color="text.tertiary">
                  Station assignment can be cleared from the staff detail
                  drawer once added. Picking a specific station during
                  reunification is tracked under ASM-278.
                </Text>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer p="0 20px 20px 20px">
              <HStack justify="flex-end" gap={2} width="100%">
                <Button
                  visual="secondary"
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  data-e2e="add-event-staff-cancel"
                >
                  Cancel
                </Button>
                <Button
                  visual="primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  data-e2e="add-event-staff-submit"
                >
                  {submitting ? 'Adding…' : 'Add staff'}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default AddEventStaffDialog;
